import React, { useState } from 'react';

// Plain-English maths behind every number on the Sales Intelligence page.
// Keep this in sync with backend/sales_intel.py if a formula changes.
const SECTIONS = [
  {
    id: 'source',
    title: 'Where the numbers come from',
    blurb: 'Nothing here is typed in by hand — it is a mirror of NetSuite, refreshed automatically.',
    items: [
      {
        name: 'The data mirror',
        eq: 'NetSuite (SuiteQL) → local mirror → this page',
        body: 'A sync runs once a day at midnight Melbourne time (and whenever you press “Refresh from NetSuite”) and copies quote + sales order headers, their line items, opportunities, employee list and stock on hand. Every figure below is calculated from that mirror, so the page is as current as the “last pull” time shown above the tabs.',
        notes: [
          'Quotes = NetSuite Estimates. Sales orders = NetSuite Sales Orders.',
          'Region comes from the document number prefix (QTAU… / QTUS…), so AU and US never mix.',
          'Values use the transaction total in its own currency (foreigntotal) and are labelled with the region currency — mixed-currency subsidiaries are added together as-is.',
        ],
      },
      {
        name: 'The four filters',
        eq: 'Period ∩ Segment ∩ Subsidiary ∩ Showing',
        body: 'Every table, chart and KPI is filtered the same way. Period filters on the document date (not the modified date). Segment uses the linked Opportunity’s type and falls back to the transaction’s own class. Subsidiary is the NetSuite subsidiary. “Showing” just chooses whether quote or sales-order figures are displayed.',
        notes: ['“Year to date” = 1 January of this year through today.'],
      },
      {
        name: 'Who gets the credit',
        eq: 'Created by = app stamp, else NetSuite Created By',
        body: 'Rep tables use the NetSuite Sales Rep (employee) field. Creator tables use the field this app stamps when it pushes a quote, falling back to NetSuite’s own Created By. People mapped to the “Old Employee” group are left out of all leaderboards.',
      },
    ],
  },
  {
    id: 'overview',
    title: 'Overview KPIs',
    blurb: 'The headline cards on the Overview tab.',
    items: [
      {
        name: 'Quotes raised · Total quoted',
        eq: 'COUNT(quotes in window)   ·   SUM(quote total)',
        body: 'A straight count and sum of every quote dated inside the period, after the filters. Includes quotes that are still open.',
      },
      {
        name: 'True wins (opportunity → sales order)',
        eq: 'True wins = COUNT(opportunities with a linked sales order)\nTrue win % = true wins ÷ ALL opportunities in window × 100',
        body: 'The honest win rate, measured the way the CFO asks for it: of every opportunity raised in this period, how many have produced a real sales order? During the sync we read the Opportunity field on each sales order and stamp the link on both records, so no manual status keeping is involved.',
        notes: [
          'True win value = the value of the linked SALES ORDERS (what was actually booked), falling back to the opportunity value if the order total is missing.',
          'The denominator is every opportunity in the window, including ones still open — recent periods therefore read lower, because young opportunities have had less time to convert. Compare like periods.',
          'Quote conversion % has been removed: a quote being marked Processed did not prove an order existed, so it disagreed with this measure and confused the review.',
          'An opportunity that produced several sales orders counts once, and keeps the last order linked to it.',
        ],
      },
      {
        name: 'Avg days to close',
        eq: 'AVG(sales order date − opportunity date) over true wins',
        body: 'Average calendar days between raising the opportunity and the sales order that came from it. Only true wins have this figure.',
      },
      {
        name: 'Avg GM% est.',
        eq: 'AVG(estimated gross profit % of each quote) × 100',
        body: 'The unweighted average of NetSuite’s own estimated gross profit percent on each quote in the window — every quote counts the same, whether it is $500 or $500,000.',
        notes: [
          'It is NetSuite’s ESTIMATE, taken from the quote at sync time (it uses item cost at that moment), not realised margin from an invoice.',
          'Quotes with no GM estimate are skipped rather than counted as zero.',
          'Because it is unweighted, a handful of small odd-margin quotes can move it — use the freight-amortised card beside it for the money-weighted view.',
        ],
      },
      {
        name: 'GM% freight-amortised',
        eq: 'goods GM% = (goods revenue − goods cost) ÷ goods revenue\namortised GM% = (revenue − goods cost − freight cost) ÷ revenue',
        body: 'The money-weighted margin on booked sales orders, built from LINE-level cost rather than a per-quote average. Freight lines are separated from goods, the freight we on-charge is netted against what the freight actually costs, and whatever is left over is amortised across goods revenue — so a bulk supplier shipment is carried by the items that came in it rather than sitting outside the margin.',
        notes: [
          'Line cost is NetSuite’s cost estimate on the line (purchase order rate where it has one). The card shows what share of lines carry a cost.',
          'Supplier freight is not booked on the sales order, so until purchase order / landed cost data is visible the freight cost is assumed to equal the freight charged — that stops freight revenue flattering product margin. The card’s tooltip always names the source being used.',
          'The “−x pp” on the card is how much margin freight is costing: goods-only GM minus amortised GM.',
          'Next iteration (when Purchase Order view is granted): allocate each inbound shipment’s real freight across the items on that shipment by value, so per-item margin carries its own share.',
        ],
      },
      {
        name: 'Sales orders · Order value · Avg order value',
        eq: 'COUNT(orders)  ·  SUM(order total)  ·  value ÷ count',
        body: 'Same idea as the quote cards, on sales orders dated in the period.',
      },
      {
        name: 'Daily / monthly bars',
        eq: 'GROUP BY date (≤ 95 days) or year-month (> 95 days)',
        body: 'Bars are the document count on the left scale; the amber line is the total value on the right scale. Hover any bar for the count, value, average per document, that bucket’s share of the period, and the change against the previous bucket.',
      },
      {
        name: 'Idle / stale quotes',
        eq: 'status = Open AND (today − last modified) ≥ 30 days',
        body: 'Open quotes nobody has touched in NetSuite for 30 days or more. Idle days are measured from NetSuite’s last-modified date, falling back to the quote date for older records.',
      },
    ],
  },
  {
    id: 'people',
    title: 'Team & Clients',
    blurb: 'How the rep, creator, group and client tables add up.',
    items: [
      {
        name: 'Per-person and per-client rows',
        eq: 'GROUP BY rep / creator / client → COUNT, SUM(total)',
        body: 'Each row is that person’s or client’s quotes (or orders) inside the current filters. Percentages in a table are always that row’s share of the table total.',
      },
      {
        name: 'Group totals',
        eq: 'Σ of each creator’s figures, grouped by their team',
        body: 'Teams come from the group mapping in Admin (Management, Sales Reps, Sales Support, Projects Team). Anyone unmapped shows as “Unassigned”; “Old Employee” rows are excluded.',
      },
      {
        name: 'Parent company rollup',
        eq: 'GROUP BY customer.parent',
        body: 'Uses the NetSuite customer’s parent record, so branches of the same group add together. Customers with no parent are left out of that table.',
      },
    ],
  },
  {
    id: 'items',
    title: 'Items & Stock',
    blurb: 'Demand versus what is on the shelf.',
    items: [
      {
        name: 'Quoted vs ordered quantity',
        eq: 'GROUP BY item → Σ qty where type = quote / order',
        body: 'Line-level quantities inside the period. Ranked by quoted quantity (or ordered quantity when “Sales Orders” is selected).',
      },
      {
        name: 'Generic budgetary lines',
        eq: 'first 3 dash-sections of the line’s display name',
        body: 'Lines booked against a “Generic …” placeholder item are re-pointed at the real product code found in the line description (e.g. LPA-SR-C102), so demand lands on the actual product instead of a generic bucket. Those rows are tagged “from generic”.',
        notes: ['Generic-resolved rows have no stock figure, because the placeholder item is not the stocked item.'],
      },
      {
        name: '“Below demand” flag',
        eq: 'on hand < (quoted qty + ordered qty) in the period',
        body: 'A simple early warning that what has been asked for in this window already exceeds the quantity on hand. It is not a full MRP calculation — it ignores POs in transit and committed stock.',
      },
      {
        name: 'Item demand forecast',
        eq: 'same weighted average as Sales/Quote Forecasts, per item, on ORDERED quantity (and separately on quoted qty)',
        body: 'Every item row uses the same period buttons, buckets and weights as the Sales/Quote Forecasts tab, applied to ORDERED quantity only — quoted quantity is deliberately not projected, because many quotes never convert. “Sold in period” is the raw quantity on sales orders inside the window; “Avg per month” is sold ÷ number of months in the period (zero months included). Momentum compares the newest half of the period with the older half. The “Short on stock only” toggle keeps rows whose shortfall is positive; the Excel export lists every item with demand (Short-on-stock sheet first). Click a row for the full breakdown: a dot per sales order, order history, stock position and the outliers.',
        notes: ['Unresolved “Generic …” placeholder items are kept in the ranking because their demand is real — they simply have no stock record, so supply reads n/a.'],
      },
      {
        name: 'Material outliers',
        eq: 'per sales order (leave-one-out): flagged when removing it moves the weighted avg by ≥ 5% AND qty ≥ 3 × the median order',
        body: 'One freak project order can double a weighted average, so we detect them at the individual-order level. Both tests must pass — the order is several times a typical order for that item AND it actually sways the weighted average — which keeps a merely busy month from being flagged. Outliers stay IN the forecast by default; untick one in the item drilldown to see the weighting without it.',
        notes: [
          'The item table and the drilldown use the identical test, so the “n outliers in” tag always matches the drilldown’s outlier list. Hover the tag to see what the figure would be with those orders removed.',
        ],
      },
      {
        name: 'Item stock figures',
        eq: 'start of period · on purchase orders · sold · current balance · open on order',
        body: 'Current balance and in-transit come from NetSuite item + location records on the nightly sync. Open on order is committed plus backordered quantity (falling back to our own synced open sales orders). Sold is the quantity on sales orders inside the period.',
        notes: [
          'NetSuite does not expose historical stock balances, so the app now writes its own dated stock snapshot on every sync. Until a snapshot exists for the start of your chosen period, “start of stock balance” is estimated as today’s balance plus what sold in the period, and is tagged “est”.',
          'Quantities on purchase orders need “Transactions → Purchase Order (View)” on the NetSuite integration role. Until IT adds it, that column reads “—” and the page says so.',
        ],
      },
      {
        name: 'Stock shortfall',
        eq: 'forecast ordered qty (all orders in) − on hand (when positive)',
        body: 'How short you would be over the next period if the forecast lands and nothing else arrives. Based on ORDERED demand (real movement), not quoted demand.',
      },
      {
        name: 'Suggested reorder',
        eq: 'reorder = ⌈ forecast ordered × (1 + safety buffer %) − (on hand + in transit + on PO − open on order) ⌉, floored at 0',
        body: 'Turns the shortfall into a buy quantity. The target is the forecast for the next period plus the safety buffer you set (default 20%, remembered in your browser). Everything already available is deducted: stock on hand, stock in transit and on purchase orders (when NetSuite exposes them), less what is already promised on open sales orders. Rounded up to whole units. Hover any reorder figure for the working; the Excel export lists the reorder list first.',
        notes: ['A bigger buffer protects against demand spikes at the cost of holding more stock; 0% reorders to the forecast exactly.', 'No lead time or minimum order quantity yet — those need supplier data that is not in NetSuite for us.'],
      },
    ],
  },
  {
    id: 'forecast',
    title: 'Sales/Quote Forecasts',
    blurb: 'Weighted quote and sales-order projections over the period you choose — 30 days, 3 months, 6 months, year to date or 12 months. Item forecasts live under Items & Stock.',
    items: [
      {
        name: 'Choosing the period',
        eq: '30 days → four 7-day buckets · 3 / 6 / 12 months and YTD → complete calendar months',
        body: 'The buttons above the forecast pick both the history it learns from and the length of what it projects: choose 3 months and you get “expected over the next 3 months”, built from the last 3 complete months. Part-months are never used as a bucket, so a period that is only a few days old cannot drag the weighting down. Year to date uses every complete month since 1 January.',
        notes: ['The window actually used is printed under the heading, so you can always see which months are in play.'],
      },
      {
        name: 'The weighted average',
        eq: 'weighted avg per bucket = Σ (value × bucket position) ÷ Σ (bucket positions)\nforecast = weighted avg × number of buckets',
        body: 'Buckets are weighted by how recent they are: with 3 months the split is 1/6, 2/6, 3/6, so last month counts three times as much as the month before last. The chart draws the projection as one striped bar per future month (or week for 30 days), each at the weighted per-bucket average, after a dashed “forecast →” divider; the headline number is that bar × the number of buckets.',
        notes: ['It is a weighted trend, not a commitment — no seasonality and no pipeline weighting.', '“vs actual” compares the projection with what was really quoted/ordered in the look-back period; “Momentum” compares the newest half of the period with the older half; “Implied quote → order value conversion” is expected order value ÷ expected quote value.'],
      },
      {
        name: 'Forecast accuracy (backtest)',
        eq: '|predicted − actual| ÷ actual × 100, for the newest bucket',
        body: 'We re-run the same weighting using only the buckets BEFORE the newest one and compare its prediction with what actually happened. That error % is how much to trust the current projection.',
      },
      {
        name: 'Probability-weighted pipeline',
        eq: 'Σ (open opportunity value × its win probability)',
        body: 'Shown as its own line so it never double-counts the quote/order projections. The blended % is expected value ÷ open value — see Win Probability below for how each probability is scored.',
      },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    blurb: 'Booked revenue month by month, with honest comparisons.',
    items: [
      {
        name: 'Monthly revenue & average order',
        eq: 'Σ order totals per calendar month  ·  revenue ÷ order count',
        body: 'Months are built from the live 12-month mirror plus a permanent monthly archive, so history keeps growing past the mirror window. The first, partial month of the mirror is dropped so it can never be compared as if it were complete.',
      },
      {
        name: 'MoM %',
        eq: '(this month − previous month) ÷ previous month × 100',
        body: 'For the month in progress we compare like-for-like instead: the 1st→today of this month against the 1st→same day of last month (the row is flagged MTD).',
      },
      {
        name: 'YoY % / quarter / YTD vs LY',
        eq: '(this period − same period last year) ÷ same period last year × 100',
        body: 'Only calculated when last year’s month is fully logged in the archive — otherwise the cell reads “LY not fully logged yet” rather than printing a fake number. Quarter and YTD comparisons require every month in the period to be fully logged.',
        notes: ['This is why very large, obviously wrong percentages no longer appear — an incomplete base month is refused.'],
      },
      {
        name: 'Monthly GM%',
        eq: 'AVG(estimated GM% of each order in the month)',
        body: 'Same estimated gross profit percent as above, averaged over the orders in that month. Archived months keep a running sum and count so the average survives past the mirror window.',
      },
    ],
  },
  {
    id: 'probability',
    title: 'Win Probability',
    blurb: 'An empirical model trained on your own closed opportunities — no vendor black box.',
    items: [
      {
        name: 'What it is trained on',
        eq: 'every opportunity closed won / lost in the last ~3 years',
        body: 'For each subsidiary with 200+ closed opportunities we build its own table; smaller subsidiaries fall back to the global table. The base rate is simply won ÷ closed.',
      },
      {
        name: 'The model (log-odds)',
        eq: 'z = logit(base rate) + Σ [ logit(rate for this factor) − logit(base rate) ]\nmodel p = 1 ÷ (1 + e^−z)',
        body: 'Five factors each nudge the odds up or down: how long the opportunity has been open (banded), its value (banded), sector, whether the client is new or repeat, and the sales rep’s own historical win rate. A factor is only allowed to move the number when it has at least 20 closed examples behind it — thin evidence is ignored.',
        notes: ['Probabilities are clamped between 2% and 98% before the maths, so a single perfect run of luck can never produce a 0% or 100% factor.'],
      },
      {
        name: 'Recency overlay',
        eq: 'win % = model p × recency multiplier (capped at 99%)',
        body: 'Multipliers by days since last NetSuite activity: 0–14d ×1.05, 15–60d ×1.00, 61–150d ×0.90, 151–300d ×0.75, 300d+ ×0.55. A deal going quiet is the strongest signal that it is slipping.',
      },
      {
        name: 'Tiers & expected value',
        eq: 'High ≥ 75% · Medium 40–75% · Low < 40%\nExpected value = Σ (value × win %)',
        body: 'The blended % is expected value ÷ total open value — effectively the probability-weighted worth of the whole open pipeline.',
      },
      {
        name: 'Decline alerts',
        eq: 'win % today < win % ~7 days ago, for opportunities ≥ $50k',
        body: 'Every scoring run saves a daily snapshot of each open opportunity’s probability (kept 120 days). Alerts compare today against the most recent snapshot at least 7 days old, ranked by expected impact = value × probability drop.',
      },
    ],
  },
  {
    id: 'insights',
    title: 'Deep Insights',
    blurb: 'Pipeline health, funnel, distributions and movers.',
    items: [
      {
        name: 'Live pipeline & aging',
        eq: 'ALL open quotes (not period-limited), bucketed by idle days',
        body: 'Deliberately ignores the Period filter — an open quote from eight months ago is still live money. Buckets are 0–30, 31–60, 61–90 and 90+ days since last modification.',
      },
      {
        name: 'Team leaderboard',
        eq: 'per creator: quotes, quoted $, true wins, win % = wins ÷ quotes, avg close days, avg GM%',
        body: 'Same true-win definition as the Overview card, per person, within the period.',
      },
      {
        name: 'Funnel',
        eq: 'COUNT + Σ value grouped by quote status',
        body: 'Where the period’s quotes currently sit: Open, Processed, Closed, Expired.',
      },
      {
        name: 'Quote size & close-time distributions',
        eq: 'fixed buckets: <$1k, $1–5k, $5–20k, $20–100k, $100k+ · 0–7, 8–30, 31–90, 90+ days',
        body: 'Counts and values per bucket, so you can see whether the business is won on volume or on a few large deals.',
      },
      {
        name: 'Item movers',
        eq: '(recent half − previous half) ÷ previous half × 100',
        body: 'The period is split in two halves and quantities compared. Items with fewer than 20 units total across the whole period are ignored as noise, change is capped at +999% / −100%, and an item with no prior-half activity is marked “new”.',
      },
      {
        name: 'Client counts',
        eq: 'active = quoted in period · new = first ever quote inside period · returning = active − new',
        body: '“First ever” looks across all history in the mirror, not just the current window.',
      },
    ],
  },
];

export default function SalesIntelMaths() {
  const [open, setOpen] = useState(() => ({ source: true, overview: true }));
  const toggle = id => setOpen(o => ({ ...o, [id]: !o[id] }));
  return (
    <div className="si-maths" data-testid="si-maths">
      <p className="si-maths-intro">
        Every formula on this page, in plain English. If a number ever looks wrong, start here —
        most surprises come from a definition (what counts as a win, which months are comparable)
        rather than from bad data.
      </p>
      <div className="si-maths-nav">
        {SECTIONS.map(s => (
          <button key={s.id} className="si-chip" data-testid={'si-maths-jump-' + s.id}
            onClick={() => { setOpen(o => ({ ...o, [s.id]: true })); const el = document.getElementById('maths-' + s.id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
            {s.title}
          </button>
        ))}
      </div>
      {SECTIONS.map(s => (
        <section key={s.id} id={'maths-' + s.id} className="si-maths-sec" data-testid={'si-maths-sec-' + s.id}>
          <button className="si-maths-head" onClick={() => toggle(s.id)} data-testid={'si-maths-toggle-' + s.id}
            aria-expanded={!!open[s.id]}>
            <span className="si-maths-caret">{open[s.id] ? '▾' : '▸'}</span>
            <span>
              <strong>{s.title}</strong>
              <em>{s.blurb}</em>
            </span>
            <span className="si-maths-count">{s.items.length}</span>
          </button>
          {open[s.id] && (
            <div className="si-maths-body">
              {s.items.map(it => (
                <article key={it.name} className="si-maths-card">
                  <h5>{it.name}</h5>
                  <pre className="si-maths-eq">{it.eq}</pre>
                  <p>{it.body}</p>
                  {it.notes && (
                    <ul className="si-maths-notes">
                      {it.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
